import { useEffect, useState } from 'react';
import { formatClock } from '../lib/useSmoothClock.js';
import { color } from '../theme.js';

const RECONNECT_WINDOW_MS = 10_000;

/**
 * Shown while OUR socket is down mid-race.
 *
 * The copy is load-bearing, not decoration: the server clock genuinely does not
 * pause on disconnect (plan doc §8.4), so telling the player their time is still
 * burning is the honest thing to do — and explains why they may return to a much
 * shorter clock.
 */
export function DisconnectOverlay({ remainingMs }: { remainingMs: number }) {
  const [left, setLeft] = useState(RECONNECT_WINDOW_MS);

  useEffect(() => {
    const started = performance.now();
    const t = setInterval(() => {
      setLeft(Math.max(0, RECONNECT_WINDOW_MS - (performance.now() - started)));
    }, 100);
    return () => clearInterval(t);
  }, []);

  const pct = (left / RECONNECT_WINDOW_MS) * 100;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        containerType: 'size',
        fontFamily: "'Chakra Petch',system-ui,sans-serif",
      }}
    >
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'rgba(5,7,5,.72)', backdropFilter: 'blur(2px)' }} />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          boxShadow: 'inset 0 0 clamp(60px,18cqmin,220px) rgba(255,176,32,.28)',
          animation: 'dcVig 1.4s ease-in-out infinite',
        }}
      />

      {/* the clock keeps running — show it */}
      <div style={{ position: 'absolute', top: 24, right: 24, zIndex: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, opacity: 0.9 }}>
        <span style={{ fontSize: 10, letterSpacing: '.24em', fontWeight: 700, color: color.amber }}>CLOCK STILL RUNNING</span>
        <div style={{ padding: '6px 14px', border: '1px solid rgba(255,176,32,.4)', borderRadius: 10, background: 'rgba(255,176,32,.08)' }}>
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 34, lineHeight: 1, color: color.amberBright, animation: 'dcBlink 1s steps(1,end) infinite' }}>
            {formatClock(remainingMs)}
          </span>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 5, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div
          style={{
            width: 'min(92vw,460px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 20,
            padding: 34,
            border: '1px solid rgba(255,176,32,.35)',
            borderRadius: 18,
            background: 'linear-gradient(180deg,rgba(20,17,8,.96),rgba(12,11,7,.96))',
            boxShadow: '0 20px 60px rgba(0,0,0,.6)',
          }}
        >
          <div style={{ width: 72, height: 72, borderRadius: '50%', border: '4px solid rgba(255,176,32,.18)', borderTopColor: color.amber, animation: 'dcSpin .9s linear infinite' }} />

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 26, letterSpacing: '.05em', color: color.amberBright }}>CONNECTION LOST</span>
            <span style={{ fontSize: 14, color: 'rgba(234,255,234,.6)', fontWeight: 500, lineHeight: 1.45 }}>
              Reconnecting to the match… your clock is <span style={{ color: color.amber, fontWeight: 700 }}>still counting down</span>.
            </span>
          </div>

          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, letterSpacing: '.14em', fontWeight: 600, color: 'rgba(234,255,234,.5)' }}>RECONNECT WINDOW</span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 17, color: color.redBright }}>
                {Math.ceil(left / 1000)}s
              </span>
            </div>
            <div style={{ width: '100%', height: 8, borderRadius: 999, background: 'rgba(255,255,255,.06)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 999, background: `linear-gradient(90deg,${color.amber},${color.redBright})`, transition: 'width .1s linear' }} />
            </div>
            <span style={{ fontSize: 11, color: 'rgba(234,255,234,.4)', textAlign: 'center', lineHeight: 1.4 }}>
              Past the window this counts as a <span style={{ color: color.redBright, fontWeight: 700 }}>forfeit</span> — opponent takes the win.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** A small banner when the OPPONENT drops. We keep playing. */
export function OpponentDisconnectedBanner() {
  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 18px',
        border: '1px solid rgba(255,176,32,.5)',
        borderRadius: 999,
        background: 'linear-gradient(180deg,rgba(38,26,6,.94),rgba(28,19,4,.94))',
        fontFamily: "'Chakra Petch',system-ui,sans-serif",
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color.amber, animation: 'dcBlink 1s steps(1,end) infinite' }} />
      <span style={{ fontWeight: 700, fontSize: 13, letterSpacing: '.06em', color: color.amberBright }}>OPPONENT DISCONNECTED</span>
      <span style={{ fontSize: 12, color: 'rgba(234,255,234,.6)' }}>— they forfeit if they don't return</span>
    </div>
  );
}
